import TextBubble from "./text-bubble";
import {ReactElement, useMemo} from "react";
import ExpandableTitle from "./expandable-title";
import { AnswerSource, AnswerStats, Feedback, SurveyStatus } from "../../redux/survey/survey.types";
import {feedbackListForFilter} from "../../pages/view-survey/view-survey.helpers";
import FeedbackEmptyState from "./feedback-empty-state.component";
import { answerTitle } from "../../pages/view-survey/components/results-question.helpers";

const { AI, All, Human, RTR } = AnswerSource;

interface FeedbackListProps {
  readonly answers: AnswerStats[];
  readonly sourceFilter: AnswerSource;
  readonly surveyStatus?: SurveyStatus;
  readonly useRedesign?: boolean
}

interface AnswerFeedbackGroup {
  title: string;
  imageUrl?: string;
  allFeedback: Feedback[];
}

const FeedbackList = ({ answers, sourceFilter, surveyStatus, useRedesign = false }: FeedbackListProps) => {
  const visibleFeedbackLists = useMemo(() => {
    const feedbackLists: AnswerFeedbackGroup[] = [];

    answers.forEach((answer, index) => {
      if (answer.feedbacks) {
        const { title, feedbacks, imageUrl } = answer;
        const allFeedback = feedbackListForFilter(feedbacks, sourceFilter);
        allFeedback.length && feedbackLists.push({ title: answerTitle(title, index, surveyStatus), allFeedback, imageUrl });
      }
    });

    return feedbackLists;
  }, [answers, sourceFilter]);

  const renderList = (): ReactElement => {
    return (
      <ul className={`flex flex-col px-4 ${useRedesign ? '' : 'pb-4'}`}>
        {visibleFeedbackLists.map(renderAnswerReasons)}
      </ul>
    );
  };

  const renderAnswerReasons = (answer: AnswerFeedbackGroup): ReactElement | null => {
    if (!answer.allFeedback.length) {
      return null;
    }

    const { title, allFeedback } = answer;
    const nonNullTitle = title || '';
    const displayTitle = allFeedback.length > 0 ? `${nonNullTitle} (${allFeedback.length})` : nonNullTitle;

    return (
      <ExpandableTitle
        title={displayTitle}
        nested={!useRedesign}
        key={`${title}-parent`}
        imgSrc={answer.imageUrl}
      >
        <div className={`flex flex-col gap-2 px-4 ${useRedesign ? 'mb-6' : ''}`}>
          <ul className="flex flex-col gap-2">
            {allFeedback.map(({ feedback, createdAt, userType }, index) => (
              <TextBubble
                feedback={feedback}
                createdAt={createdAt}
                userType={userType}
                key={`${title}-${index}`}
                useRedesign={useRedesign}
              />
            ))}
          </ul>
        </div>
      </ExpandableTitle>
    );
  };

  const renderEmptyState = (): ReactElement => {
    if (!useRedesign) {
      return (
        <div className="flex flex-row justify-center px-4 pb-4 text-gray-501 text-sm">
          No open-ended responses for selected respondent type.
        </div>
      );
    }

    return (<FeedbackEmptyState />);
  };

  const renderContent = () => {
    if (visibleFeedbackLists.length) {
      return renderList();
    } else {
      return renderEmptyState();
    }
  };

  return (
    <>
      { useRedesign ? (renderContent()) : (
        <ExpandableTitle title={'Open-Ended Responses'}>
          {renderContent()}
        </ExpandableTitle>
      )
      }
    </>
  );
};

export default FeedbackList;
